import React from 'react';
import {
  makeStyles,
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Divider,
  Hidden,
} from '@material-ui/core';
import CalculateIcon from 'components/shared/icons/CalculateIcon';
import UserCheckIcon from 'components/shared/icons/UserCheck';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Image from 'next/image';
import { i18n } from 'config/i18n';
import getERUrl from 'utils/getERUrl';
import getLocalizedUrl from 'utils/getLocalizedUrl';
import gotQs from '../../public/imgs/list-your-bike/got_questions.webp';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    marginTop: theme.spacing(13),
    marginBottom: theme.spacing(13),
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(22),
      marginTop: theme.spacing(18),
    },
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
      marginBottom: theme.spacing(5.5),
    },
  },
  title: {
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(0),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(36),
      marginBottom: theme.spacing(4),
      textAlign: 'left',
    },
    [theme.breakpoints.up('lg')]: {
      textAlign: 'center',
      marginBottom: theme.spacing(13),
    },
  },
  imgGrid: {
    [theme.breakpoints.up('lg')]: {
      order: 1,
    },
  },
  txtGrid: {
    marginTop: theme.spacing(4.75),
    [theme.breakpoints.up('lg')]: {
      order: 2,
      marginTop: 0,
    },
  },
  questionsContainer: {
    padding: theme.spacing(0, 0, 4.5),
    width: 'auto',
    marginLeft: 0,
    [theme.breakpoints.only('sm')]: {
      textAlign: 'center',
    },
    [theme.breakpoints.up('lg')]: {
      marginLeft: '25%',
    },
  },
  questionIcn: {
    '& .MuiSvgIcon-root': {
      width: theme.spacing(6),
      height: theme.spacing(6),
      color: theme.palette.primary.main,
      [theme.breakpoints.only('sm')]: {
        width: theme.spacing(4),
        height: theme.spacing(4),
      },
    },
  },
  questionTitle: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: '500',
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  questionDesc: {
    letterSpacing: theme.typography.pxToRem(0.5),
    lineHeight: theme.typography.pxToRem(24),
    marginTop: theme.spacing(1.5),
    fontSize: theme.typography.pxToRem(14),
    '& a': {
      color: theme.palette.secondary.main,
      fontWeight: 'bold',
    },
  },
  moreAnswers: {
    padding: 0,
    background: 'none !important',
    textDecoration: 'underline',
    marginLeft: 0,
    [theme.breakpoints.only('sm')]: {
      textAlign: 'center',
      display: 'flex',
    },
    [theme.breakpoints.up('lg')]: {
      marginLeft: '25%',
    },
  },
  imgContainer: {
    height: theme.spacing(42.5),
    marginTop: theme.spacing(5.5),
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 20,
    [theme.breakpoints.up('lg')]: {
      height: '100%',
      marginTop: 0,
    },
    [theme.breakpoints.only('sm')]: {
      height: theme.spacing(20),
      marginTop: theme.spacing(2.75),
      borderRadius: 15,
    },
  },
  img: {
    minHeight: '100% !important',
    maxWidth: '100% !important',
    position: 'absolute !important',
    margin: '0 !important',
    height: 'auto !important',
    maxHeight: 'none !important',
    width: 'auto !important',
    [theme.breakpoints.up('lg')]: {
      height: '100% !important',
      left: '0 !important',
      maxWidth: 'none !important',
    },
  },
  divider: {
    marginTop: theme.spacing(10),
    backgroundColor: theme.palette.grey[200],
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
    },
  },

}));

const QuestionsSection = ({ i18n: t }) => {
  const classes = useStyles();

  const questionsIcn = [<CalculateIcon />, <UserCheckIcon />, <MonetizationOnIcon />];

  return (
    <>
      <Container maxWidth="xl" classes={{ root: classes.mainContainer }}>
        <Typography variant="h2" classes={{ root: classes.title }}>
          {t('fe_es_questions_section:title')}
        </Typography>
        <Grid container alignItems="stretch" spacing={2}>
          <Grid item xs={12} lg={7} classes={{ root: classes.txtGrid }}>
            <Box className={classes.questionsContainer}>
              <Box className={classes.questionIcn}>{questionsIcn[0]}</Box>
              <Typography variant="h4" classes={{ root: classes.questionTitle }}>
                {t('fe_es_questions_section:questions.q1')}
              </Typography>
              <Typography
                classes={{ root: classes.questionDesc }}
                dangerouslySetInnerHTML={{
                  __html: t('fe_es_questions_section:questions.a1', {
                    er_path: 'https://www.eaglerider.com',
                  }),
                }}
              />
            </Box>
            <Box className={classes.questionsContainer}>
              <Box className={classes.questionIcn}>{questionsIcn[1]}</Box>
              <Typography variant="h4" classes={{ root: classes.questionTitle }}>
                {t('fe_es_questions_section:questions.q2')}
              </Typography>
              <Typography
                classes={{ root: classes.questionDesc }}
                dangerouslySetInnerHTML={{
                  __html: t('fe_es_questions_section:questions.a2', {
                    club_path: getERUrl(`/${t('routes:motorcycle-subscription')}`, i18n.language),
                    commercial_host_path: 'https://www.eagleshare.com/commercial-host',
                  }),
                }}
              />
            </Box>
            <Box className={classes.questionsContainer}>
              <Box className={classes.questionIcn}>{questionsIcn[2]}</Box>
              <Typography variant="h4" classes={{ root: classes.questionTitle }}>
                {t('fe_es_questions_section:questions.q3')}
              </Typography>
              <Typography
                classes={{ root: classes.questionDesc }}
                dangerouslySetInnerHTML={{
                  __html: t('fe_es_questions_section:questions.a3', {
                    club_path: getERUrl(`/${t('routes:motorcycle-subscription')}`, i18n.language),
                    commercial_host_path: '/commercial-host',
                  }),
                }}
              />
            </Box>
            <Button
              classes={{ root: classes.moreAnswers }}
              target="_blank"
              href={getLocalizedUrl('/faqs/eagleshare-private-owner', i18n.language)}
              endIcon={<ArrowForwardIcon />}
            >
              {t('fe_es_questions_section:find_more_answers')}
            </Button>
          </Grid>
          <Grid item xs={12} lg={5} classes={{ root: classes.imgGrid }}>
            <Box className={classes.imgContainer}>
              <Image
                className={classes.img}
                src={gotQs}
                alt={t('fe_es_questions_section:title')}
                layout="fill"
                unoptimized
                placeholder="blur"
              />
            </Box>
          </Grid>
        </Grid>

        <Hidden lgUp>
          <Divider classes={{ root: classes.divider }} />
        </Hidden>
      </Container>
    </>
  );
};

export default QuestionsSection;
